import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import { INSPECTION_LINKS_ENDPOINT } from "@/constants/Endpoints";
import axios from "axios";
import { LOGGER } from "@/util/logger";
import { clone } from "@/util/clone";
import { MODULE_TYPES } from "@/constants/InspectionConstants";
import { INSPECTION_LINKS_PERMISSIONS } from "@/rbac";
import { ACCESS_LEVELS } from "@/rbac/accessLevels.js";
import { useUserPermissionStore } from "@/stores/userPermission";
import { useQuickSearchStore } from "@/stores/quickSearch";
import { useInspectionStore } from "@/stores/inspection";
import { useInspectionLinksStore } from "@/stores/inspectionLinks";
import { convertStructureUnitsToLocations, filterStructureUnitsByLegacyType } from "@/composables/spanBySpan";
import { INSPECTION_STATUSES } from "@/constants/InspectionStatuses";
import { sortStructure } from "@/util/sortStructure.js";
import { STRUCTURE_UNIT_TYPE } from "@/constants/Inspections";

export const useSuperstructureStore = defineStore("superStructure", () => {
  const quickSearchStore = useQuickSearchStore();
  const userPermissionStore = useUserPermissionStore();
  const inspectionStore = useInspectionStore();
  const inspectionLinksStore = useInspectionLinksStore();
  let superStructureData = ref({});
  let allPanels = [0, 1, 2];
  const moduleType = ref(MODULE_TYPES.INSPECTION);
  let panel = ref(allPanels);
  const isEditable = ref(false);
  const isCurvedBridgeEditable = ref(false);
  const structureHeader = computed(() => quickSearchStore.getStructure());
  const isDirty = ref(false);
  const brkey = ref(null);
  const locations = ref([]);
  const reportGroup = new Set(["S1", "S2", "L1", "A1"]);
  const toggleAllPanels = (expandAll) => {
    if (expandAll) {
      panel.value = [...allPanels];
    } else {
      panel.value = [];
    }
  };

  const hasAccessToEdit = computed({
    get() {
      if (structureHeader.value?.INSPECTIONSTATUS === INSPECTION_STATUSES.ACCEPTED) {
        return userPermissionStore.checkUserPermission(
          INSPECTION_LINKS_PERMISSIONS[ACCESS_LEVELS.CUSTOM.EDIT_APPROVED_INSPECTION],
          userPermissionStore.getUserPermissionByBrkeyFromCache(superStructureData.value.brkey),
        );
      } else {
        return userPermissionStore.checkUserPermission(
          INSPECTION_LINKS_PERMISSIONS[ACCESS_LEVELS.EDIT],
          userPermissionStore.getUserPermissionByBrkeyFromCache(superStructureData.value.brkey),
        );
      }
    },
  });

  const setEdit = () => {
    if (moduleType.value == MODULE_TYPES.MANAGEMENT) {
      isEditable.value =
        quickSearchStore.hasQuickSearchSucceeded && superStructureData.value?.brkey && hasAccessToEdit.value;
      isCurvedBridgeEditable.value = reportGroup.has(structureHeader.value.REPORTGROUP);
    } else {
      isEditable.value = inspectionStore.isEditable;
    }
  };

  const _setDefault = (count) => {
    if (count === 0) {
      superStructureData.value.structureUnits = [{ strUnitKey: -1 }];
    }
  };

  const fetchData = async (moduleName) => {
    isDirty.value = false;
    if (moduleName === MODULE_TYPES.MANAGEMENT && structureHeader.value?.BRKEY) {
      if (!quickSearchStore.hasQuickSearchSucceeded) {
        _setDefault(0);
        return;
      }
      brkey.value = structureHeader.value.BRKEY;
      await _loadSuperstructureObject(moduleName, structureHeader.value.BRKEY, structureHeader.value.INSPECTIONID);
    } else if (moduleName === MODULE_TYPES.INSPECTION) {
      const structure = computed(() => inspectionStore.selectedInspection);
      brkey.value = structure.value.structureBridge.brkey;
      await _loadSuperstructureObject(moduleName, null, null, structure.value);
      inspectionStore.setDirtyFlag(false);
    }

    let count = superStructureData.value.structureUnits?.length || 0;
    if (count > 1) {
      allPanels = [0, 1, 2];
      for (let i = 1; i < count; i++) {
        allPanels.push(allPanels.length);
      }
      panel.value = [...allPanels];
    }
    _setDefault(count);
  };

  const _loadSuperstructureObject = async (moduleName, brkey, inspectionId, structure) => {
    moduleType.value = moduleName;
    if (moduleName === MODULE_TYPES.INSPECTION) {
      convertToInspModuleObject(structure);
    } else if (moduleName === MODULE_TYPES.MANAGEMENT) {
      await getInspLinkSuperstructure(brkey, inspectionId);
    }
    setEdit();
  };

  const getSuperstructureObject = () => {
    return clone(superStructureData.value);
  };

  const saveSuperstructure = async (moduleType, superStructureValue, structure) => {
    let isSuccess = false;
    if (moduleType === MODULE_TYPES.INSPECTION) {
      convertToSaveInspModuleObject(superStructureValue, structure);
      isSuccess = true;
    } else if (moduleType === MODULE_TYPES.MANAGEMENT) {
      isSuccess = await updateSuperstructure(superStructureValue);
    }
    return isSuccess;
  };

  const updateSuperstructure = async (superStructure) => {
    let isSuccess = true;
    const url = INSPECTION_LINKS_ENDPOINT.UPDATE_SUPERSTRUCTURE;
    await axios
      .put(url, superStructure)
      .then((response) => {
        LOGGER.logEvent(LOGGER.EventAction.Update, LOGGER.EventCat.RequestResponse, "Update Superstructure", response);
        superStructureData.value = { ...response.data.data };
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSuccess = false;
      });
    return isSuccess;
  };

  const convertToSaveInspModuleObject = (superStructureValue, structure) => {
    structure.inspection.supRating = superStructureValue.supRating;
    structure.userInspection.matStoreUnd = superStructureValue.matStoreUnd;
    structure.userInspection.newPaint = superStructureValue.newPaint;
    structure.userInspection.paintCondRate = superStructureValue.paintCondRate;
    structure.userInspection.paintExtentRate = superStructureValue.paintExtentRate;
    structure.userBridge.seatCleaning = superStructureValue.seatCleaning;
    structure.userBridge.seatCleaningNote = superStructureValue.seatCleaningNote;
    structure.userBridge.scuppersWithDownspouts = superStructureValue.scuppersWithDownspouts;
    structure.userBridge.scuppersWithoutDownspouts = superStructureValue.scuppersWithoutDownspouts;
    structure.structureBridge.curvedBridge = superStructureValue.curvedBridge;
    structure.structureBridge.nbisBridgeLength = superStructureValue.nbisBridgeLength;
    structure.inspectionFCs = superStructureValue.inspectionFCs;
    structure.structureUnitInspComments = superStructureValue.structureUnitInspComments;
  };

  const getStructureUnits = (structure) => {
    const sortedStructures = sortStructure(structure, "structureUnits", "strUnitLabel");
    return sortedStructures?.filter((structureUnit) => ["A", "M"].includes(structureUnit.legacyStructureType));
  };

  const getTotalNumberOfSpans = (structure) => {
    const superSpanConfigs = new Set(["A", "M"]);
    return structure?.structureUnitSpanSets?.reduce((total, spanSet) => {
      if (superSpanConfigs.has(spanSet.spanConfigDesignation?.[0])) {
        return total + spanSet?.numberOfSpans || 0;
      }
      return total;
    }, 0);
  };

  const convertToInspModuleObject = (structure) => {
    superStructureData.value = {
      supRating: structure.inspection.supRating,
      matStoreUnd: structure.userInspection.matStoreUnd,
      newPaint: structure.userInspection.newPaint,
      paintCondRate: structure.userInspection.paintCondRate,
      paintExtentRate: structure.userInspection.paintExtentRate,
      seatCleaning: structure.userBridge.seatCleaning,
      seatCleaningNote: structure.userBridge.seatCleaningNote,
      scuppersWithDownspouts: structure.userBridge.scuppersWithDownspouts,
      scuppersWithoutDownspouts: structure.userBridge.scuppersWithoutDownspouts,
      totalNumberOfSpans: getTotalNumberOfSpans(structure),
      skew: structure.structureBridge.skew,
      nbisBridgeLength: structure.structureBridge.nbisBridgeLength,
      curvedBridge: structure.structureBridge.curvedBridge,
      structureUnits: getStructureUnits(structure),
      inspectionFCs: structure.inspectionFCs,
      structureUnitInspComments: structure.structureUnitInspComments,
      inspectionId: structure.inspection.inspectionId,
    };
    locations.value = convertStructureUnitsToLocations(
      inspectionStore.getFilteredStructureUnits([STRUCTURE_UNIT_TYPE.MAIN, STRUCTURE_UNIT_TYPE.APPROACH], true),
    );
  };

  const getInspLinkSuperstructure = async (brkey, inspectionId) => {
    superStructureData.value = {};
    let url = INSPECTION_LINKS_ENDPOINT.GET_SUPERSTRUCTURE.replace("{brkey}", brkey).replace(
      "{inspectionId}",
      inspectionId,
    );
    await axios
      .get(url)
      .then((response) => {
        const data = response.data.data;
        const sortedStructures = sortStructure(data, "structureUnits", "strUnitLabel");
        superStructureData.value = { ...data, structureUnits: sortedStructures };
        locations.value = convertStructureUnitsToLocations(
          filterStructureUnitsByLegacyType(
            sortedStructures,
            [STRUCTURE_UNIT_TYPE.MAIN, STRUCTURE_UNIT_TYPE.APPROACH],
            true,
          ),
        );
      })
      .catch((e) => {
        LOGGER.logException(e);
      });
  };

  watch(
    [superStructureData, isDirty],
    () => {
      if (moduleType.value === MODULE_TYPES.MANAGEMENT) {
        inspectionLinksStore.isDirty = isDirty.value;
      } else if (moduleType.value === MODULE_TYPES.INSPECTION) {
        inspectionStore.setDirtyFlag(isDirty.value);
      }
    },
    { deep: true },
  );

  return {
    brkey,
    isDirty,
    isEditable,
    isCurvedBridgeEditable,
    panel,
    superStructureData,
    locations,
    fetchData,
    toggleAllPanels,
    getSuperstructureObject,
    saveSuperstructure,
    getStructureUnits,
    getTotalNumberOfSpans,
  };
});
